/**
 * src/pages/respond/VendorChat.js — the vendor's 1:1 chat with Marqland on
 * their quote link.
 *
 * Only this vendor's messages come back from the server. Marqland's replies
 * also reach the vendor on WhatsApp and e-mail, so they don't need to keep the
 * page open. "Sent" / "Updated" lines are shown as small centred notes.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Send } from 'lucide-react';
import { AIR } from '../../styles/airTheme';
import { FileList } from './SentResponse';

const API_BASE = process.env.REACT_APP_API_URL || '';

const when = (at) => {
  if (!at) return '';
  const d = new Date(at);
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return today ? time : `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}, ${time}`;
};

/** The server writes events about the vendor ("Sent: …"); on their page they read as "You sent …". */
const eventText = (t = '') => t.replace(/^Sent: /, 'You sent ').replace(/^Updated their response: /, 'You updated your response: ').replace(/\.$/, '');

export default function VendorChat({ inquiryId, token, chat, setChat }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const end = useRef(null);

  useEffect(() => { end.current?.scrollIntoView?.({ block: 'nearest' }); }, [chat.length]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setError('');
    try {
      const r = await axios.post(`${API_BASE}/api/inquiries/public/${inquiryId}/messages`, { token, text: t });
      setChat(r.data.chat || []);
      setText('');
    } catch (ex) {
      setError(ex.response?.data?.message || 'Your message could not be sent. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section aria-label="Chat with Marqland Studios" style={{ background: '#fff', border: `1px solid ${AIR.line2}`, borderRadius: 12, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 48px)', minHeight: 360 }}>
      <div style={{ padding: '14px 16px', borderBottom: `1px solid ${AIR.line}` }}>
        <b style={{ fontSize: 15 }}>Chat with Marqland Studios</b>
        <div style={{ fontSize: 12, color: AIR.muted, marginTop: 2 }}>Our replies also reach you on WhatsApp and e-mail.</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, background: AIR.studio }}>
        {!chat.length && <p style={{ fontSize: 13, color: AIR.muted, textAlign: 'center', margin: 'auto 0' }}>Questions about this request? Write to us here.</p>}
        {chat.map((m, i) => {
          if (m.event) {
            return <span key={i} style={{ alignSelf: 'center', fontSize: 12, color: AIR.muted, background: '#fff', border: `1px solid ${AIR.line}`, borderRadius: 40, padding: '4px 12px', textAlign: 'center' }}>{eventText(m.text)} · {when(m.at)}</span>;
          }
          const mine = m.direction === 'in'; // inbound to Marqland = written by this vendor
          return (
            <div key={i} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '85%', background: mine ? AIR.ink : '#fff', color: mine ? '#fff' : AIR.ink, border: mine ? 'none' : `1px solid ${AIR.line2}`, borderRadius: 14, padding: '9px 12px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
              {m.text}
              {m.media?.length > 0 && <div style={{ marginTop: 6 }}><FileList files={m.media} /></div>}
              <span style={{ display: 'block', fontSize: 11, marginTop: 4, opacity: 0.7 }}>{mine ? 'You' : 'Marqland'} · {when(m.at)}</span>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} style={{ display: 'flex', gap: 8, padding: 12, borderTop: `1px solid ${AIR.line}` }}>
        <input type="text" aria-label="Message Marqland" value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Message Marqland…" style={{ flex: 1, minWidth: 0 }} />
        <button type="submit" className="air-btn" disabled={sending || !text.trim()} aria-label="Send message" style={{ padding: '0 16px', minHeight: 48 }}>
          <Send size={16} />
        </button>
      </form>
      {error && <div role="alert" style={{ color: AIR.danger, fontSize: 13, padding: '0 12px 10px' }}>{error}</div>}
    </section>
  );
}
